import QuickAccessGrid from '@/components/QuickAccessGrid';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

import {
  Package,
  Truck,
  PackageCheck,
  Wallet,
  TrendingUp,
  TrendingDown,
  Calendar,
  Download,
  MapPin,
  Star,
  Bell,
  AlertCircle,
  ArrowUpRight,
  Filter,
  Activity,
  Users,
  ShoppingBag,
  Boxes
} from "lucide-react";

import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";

import { useDashboardStats } from "@/hooks/useDashboard";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { DashboardCharts } from "@/components/DashboardCharts";


const StatCard = ({ stat }: any) => (
  <Card className="hover:shadow-xl transition-all duration-300 border-0 shadow-md hover:-translate-y-1">
    <CardContent className="p-6">

      <div className="flex items-start justify-between mb-4">

        <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg`}>
          <stat.icon className="w-7 h-7 text-white" />
        </div>


        <div
          className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold ${
            stat.trend === "up"
              ? "text-emerald-600 bg-emerald-50"
              : "text-rose-600 bg-rose-50"
          }`}
        >

          {stat.trend === "up"
            ? <TrendingUp className="w-3 h-3"/>
            : <TrendingDown className="w-3 h-3"/>
          }

          {stat.change}

        </div>

      </div>


      <div>
        <p className="text-sm text-gray-500 mb-1 font-medium">
          {stat.title}
        </p>

        <p className="text-3xl font-bold text-gray-900">
          {stat.value}
        </p>
      </div>


      <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">

        <span>
          مقارنة بالأسبوع الماضي
        </span>

        <ArrowUpRight className="w-4 h-4"/>

      </div>


    </CardContent>
  </Card>
);



export default function Dashboard() {


  const navigate = useNavigate();
  const { data: dashboard } = useDashboardStats();


const { user } = useAuth();
const userName =
  user?.fullName ||
  user?.username ||
  "مدير النظام";


  const statsData = [
    {
      title: "إجمالي الشحنات",
      value: dashboard?.totalShipments ?? 0,
      change: "+12.5%",
      trend:"up",
      icon: Package,
      color:"from-blue-500 to-blue-600"
    },


    {
      title:"الشحنات الجارية",
      value: dashboard?.inTransit ?? 0,
      change:"+8.2%",
      trend:"up",
      icon:Truck,
      color:"from-orange-500 to-orange-600"
    },


    {
      title:"تم التسليم",
      value: dashboard?.delivered ?? 0,
      change:"+18.7%",
      trend:"up",
      icon:PackageCheck,
      color:"from-emerald-500 to-emerald-600"
    },


    {
      title:"إجمالي الإيرادات",
      value:
        `${dashboard?.totalRevenue ?? 0} ج.م`,
      change:"-2.4%",
      trend:"down",
      icon:Wallet,
      color:"from-purple-500 to-purple-600"
    },
    {
      title:"إجمالي الموظفين",
      value: dashboard?.totalEmployees ?? 0,
      change:"+",
      trend:"up",
      icon:Users,
      color:"from-pink-500 to-red-600"
    },
    {
      title:"إجمالي المنتجات",
      value: dashboard?.totalProducts ?? 0,
      change:"+",
      trend:"up",
      icon:Boxes,
      color:"from-yellow-500 to-amber-600"
    },
    {
      title:"إجمالي الطلبات",
      value: dashboard?.totalOrders ?? 0,
      change:"+",
      trend:"up",
      icon:ShoppingBag,
      color:"from-cyan-500 to-blue-600"
    }

  ];
  const shipmentTrend =
    dashboard?.chartData?.map((item: any) => ({
      day: item.date?.substring(8,10) || item.day,
      shipments: item.count || 0,
      delivered: item.delivered || 0
    })) || [];


  const statusDistribution = [
    {
      name: "تم التسليم",
      value: dashboard?.deliveredPercentage || 0,
      color:"#10b981"
    },
    {
      name:"قيد التوصيل",
      value:dashboard?.deliveryPercentage || 0,
      color:"#f59e0b"
    },
    {
      name:"قيد المعالجة",
      value:dashboard?.processingPercentage || 0,
      color:"#3b82f6"
    },
    {
      name:"مرتجع",
      value:dashboard?.returnedPercentage || 0,
      color:"#ef4444"
    }
  ];


  const topDelegates =
    dashboard?.topDelegates?.map((d:any,index:number)=>({
      name:d.name,
      deliveries:d.deliveries,
      rating:d.rating,
      zone:`${d.city} - ${d.area}`,
      avatar:d.name?.charAt(0),
      color:[
        "from-blue-500 to-cyan-500",
        "from-purple-500 to-pink-500",
        "from-orange-500 to-red-500",
        "from-emerald-500 to-teal-500"
      ][index % 4]
    })) || [];



  const recentActivities =
    dashboard?.recentShipments?.map((s:any)=>({
      icon:PackageCheck,
      color:"bg-emerald-100 text-emerald-600",
      title:`شحنة ${s.trackingNumber}`,
      time:new Date(s.createdAt).toLocaleString("ar-EG"),
      user:s.status
    })) || [];



  return (

    <div className="space-y-6" dir="rtl">


      <Card className="border-0 shadow-xl bg-gradient-to-l from-orange-500 via-orange-600 to-rose-600 text-white overflow-hidden relative">


        <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full -translate-x-48 -translate-y-48 blur-3xl"></div>

        <div className="absolute bottom-0 right-0 w-64 h-64 bg-yellow-400/20 rounded-full translate-x-32 translate-y-32 blur-3xl"></div>


              <CardContent className="relative p-8">


          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">


            <div>


              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur px-4 py-1.5 rounded-full text-xs font-medium mb-4 border border-white/20">


                <div className="w-2 h-2 rounded-full bg-emerald-300 animate-pulse"></div>


                <span>
                  النظام يعمل بكفاءة
                </span>


              </div>



              <h1 className="text-3xl md:text-4xl font-bold mb-2">

                مرحبًا، {userName}

              </h1>



              <p className="text-lg text-orange-50">

                لديك {dashboard?.todayShipments ?? 0} شحنة جديدة اليوم و {dashboard?.notificationsCount ?? 0} إشعارات تحتاج مراجعة

              </p>


            </div>



            <div className="flex flex-wrap gap-3">


              <Button
                onClick={() => navigate('/notifications')}
                className="bg-white text-orange-600 hover:bg-orange-50 shadow-lg h-12 px-6"
              >
                <Bell className="w-5 h-5 ml-2" />
                عرض الإشعارات
              </Button>



              <Button
                variant="outline"
                onClick={() => navigate('/reports')}
                className="bg-white/10 backdrop-blur text-white border-white/30 hover:bg-white/20 h-12 px-6"
              >
                <Download className="w-5 h-5 ml-2" />
                تقرير اليوم
              </Button>


            </div>

            <QuickAccessGrid />

          </div>


        </CardContent>


      </Card>



      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">


        {statsData.map((stat,i)=>(

          <StatCard
            key={i}
            stat={stat}
          />

        ))}


      </div>



      <DashboardCharts
        chartData={dashboard?.chartData || []}
        statusDistribution={statusDistribution}
        topDelegates={topDelegates}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">


        <Card className="lg:col-span-2 border-0 shadow-lg">


          <CardHeader>


            <div className="flex items-center justify-between">


              <div>

                <CardTitle>
                  حركة الشحنات
                </CardTitle>

                <CardDescription>
                  آخر 7 أيام
                </CardDescription>

              </div>



              <Button variant="outline" size="sm">


                <Calendar className="w-4 h-4 ml-2"/>

                آخر 7 أيام


              </Button>


            </div>


          </CardHeader>



          <CardContent>


            <ResponsiveContainer width="100%" height={300}>


              <AreaChart data={shipmentTrend}>


                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                />


                <XAxis dataKey="day"/>

                <YAxis/>


                <Tooltip/>


                <Area
                  type="monotone"
                  dataKey="shipments"
                  stroke="#3b82f6"
                  fill="#3b82f6"
                  fillOpacity={0.2}
                />


                <Area
                  type="monotone"
                  dataKey="delivered"
                  stroke="#10b981"
                  fill="#10b981"
                  fillOpacity={0.2}
                />


              </AreaChart>


            </ResponsiveContainer>


          </CardContent>


        </Card>
        <Card className="border-0 shadow-lg">

          <CardHeader>

            <CardTitle>
              توزيع الحالات
            </CardTitle>

            <CardDescription>
              هذا الشهر
            </CardDescription>

          </CardHeader>


          <CardContent>


            <ResponsiveContainer width="100%" height={220}>

              <PieChart>

                <Pie
                  data={statusDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={85}
                  dataKey="value"
                >

                  {statusDistribution.map((item,index)=>(

                    <Cell
                      key={index}
                      fill={item.color}
                    />

                  ))}

                </Pie>


                <Tooltip/>


              </PieChart>


            </ResponsiveContainer>



            <div className="space-y-3 mt-4">


              {statusDistribution.map((item,index)=>(

                <div className="flex items-center justify-between text-sm" key={index}>

                  <div className="flex items-center gap-2">

                    <div className={`w-3 h-3 rounded-full`} style={{backgroundColor:item.color}}></div>

                    <span>{item.name}</span>

                  </div>

                  <span className="font-medium text-gray-900">{item.value}%</span>

                </div>

              ))}


            </div>


          </CardContent>


        </Card>


      </div>



      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">


        <Card className="lg:col-span-2 border-0 shadow-lg">


          <CardHeader>

            <CardTitle>
              أحدث الشحنات
            </CardTitle>

            <CardDescription>
              آخر 10 شحنات في النظام
            </CardDescription>

          </CardHeader>


          <CardContent>

            <div className="space-y-4">

              {recentActivities.map((activity:any,index:number)=>(

                <div className="flex items-center gap-3" key={index}>

                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${activity.color}`}>

                    <activity.icon className="w-5 h-5"/>

                  </div>

                  <div>

                    <p className="text-sm font-medium text-gray-900">{activity.title}</p>

                    <p className="text-xs text-gray-500">{activity.user} - {activity.time}</p>

                  </div>

                </div>

              ))}

            </div>

          </CardContent>

        </Card>


        <Card className="border-0 shadow-lg">

          <CardHeader>

            <CardTitle>
              أفضل المندوبين
            </CardTitle>

            <CardDescription>
              أعلى 5 مندوبين تقييماً
            </CardDescription>

          </CardHeader>


          <CardContent>

            <div className="space-y-4">

              {topDelegates.map((delegate:any,index:number)=>(

                <div className="flex items-center justify-between" key={index}>

                  <div className="flex items-center gap-3">

                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg bg-gradient-to-br ${delegate.color}`}>

                      {delegate.avatar}

                    </div>

                    <div>

                      <p className="text-sm font-medium text-gray-900">{delegate.name}</p>

                      <p className="text-xs text-gray-500">{delegate.zone}</p>

                    </div>

                  </div>

                  <div className="flex items-center gap-1 text-sm text-gray-600">

                    <Star className="w-4 h-4 text-yellow-400"/>

                    <span>{delegate.rating} ({delegate.deliveries})</span>

                  </div>

                </div>

              ))}

            </div>

          </CardContent>

        </Card>

      </div>



    </div>

  );
}
